import { useCallback, useEffect, useRef, useState } from 'react';
import { When } from 'react-if';
import styled from 'styled-components6';
import { SCROLL_BUTTON_HEIGHT, ScrollButton } from './ScrollButtons';
import { mqDown, mqUp } from '../../theme';

const ContainerDiv = styled.div`
    user-select: none;
    display: grid;
    width: 100%;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-self: flex-end;
    gap: 8px;
    ${mqDown('xs')} {
        display: none;
    }
    ${mqUp('xs')} {
        display: flex;
        height: ${SCROLL_BUTTON_HEIGHT}px;
    }
`;

const MobileButtonContainer = styled.div<{ height: number }>`
    ${mqDown('xs')} {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        position: absolute;
        width: 100%;
        align-items: center;
        height: ${(props) => props.height}px;
        pointer-events: none;
        & > * {
            pointer-events: auto; /* Enable interactions for child elements (buttons) */
        }
    }
    ${mqUp('xs')} {
        display: none;
    }
`;

const CarouselContainerDiv = styled.div<{ height: number }>`
    inset: 0;
    padding: var(--rwl-spacing-md) 0;
    overflow-x: auto;
    scroll-snap-type: x mandatory;

    overflow-y: hidden;
    width: 100%;

    ::-webkit-scrollbar {
        display: none;
    }

    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    align-content: flex-start;
    gap: var(--rwl-spacing-lg);

    & > * {
        scroll-snap-align: start;
    }

    scrollbar-width: none;
    height: ${(props) => props.height}px;
`;

export interface CarouselProps<T> {
    content: T[];
    render: (item: T) => JSX.Element;
    onSelectChange?: (selectedIndex: number, numberInView: number) => void;
    onNextClick?: () => void;
    onPrevClick?: () => void;
    height?: number;
}

export const Carousel = <T,>({
    content,
    render,
    onSelectChange,
    onNextClick = () => void 0,
    onPrevClick = () => void 0,
    height = 220,
}: CarouselProps<T>) => {
    // The 'selected' prop indicates the place in the content array
    // which is furtherst most left in the user's view
    const scrollContainer = useRef<HTMLDivElement>();
    const numberOfContentInView = calculateNumberOfContentInView(
        scrollContainer.current,
        content.length
    );

    useEffect(() => {
        // Reset scroll to the beginning when content changes
        if (scrollContainer.current) {
            scrollContainer.current.scrollTo({
                left: 0,
                behavior: 'auto',
            });
        }
    }, [content]);

    const [selected, setSelected] = useState<number>(0);

    // Check if there is content to the left of the current view
    const isContentLeft: boolean = content.length > 1 && selected > 0;
    // Check if there is content to the right of the current view
    const isContentRight = (): boolean => {
        if (!scrollContainer.current) {
            return false;
        }
        const remaining = content.length - selected - 1;
        return remaining >= numberOfContentInView;
    };
    const [disableRightArrow, setDisableRightArrow] = useState(
        !isContentRight()
    );

    useEffect(() => {
        if (onSelectChange) {
            onSelectChange(selected, numberOfContentInView);
        }
        setDisableRightArrow(!isContentRight());
    }, [selected, scrollContainer.current]);

    const setContainerRef = useCallback((div: HTMLDivElement) => {
        if (!div) {
            return;
        }
        scrollContainer.current = div;
    }, []);

    const onScroll = useCallback(() => {
        if (!scrollContainer.current) {
            return;
        }

        const scrollLeft = scrollContainer.current.scrollLeft;
        const scrollWidth = scrollContainer.current.scrollWidth;

        setSelected(
            Math.floor(Math.ceil(scrollLeft) / (scrollWidth / content.length))
        );
    }, [content]);

    const scroll = (e: React.MouseEvent, scrollLeft?: boolean) => {
        if (!scrollContainer.current) {
            return;
        }
        const visibleWidth = scrollContainer.current.offsetWidth;
        scrollContainer.current.scrollBy({
            left: scrollLeft ? -visibleWidth : visibleWidth,
            behavior: 'smooth',
        });

        e.stopPropagation();
    };

    return (
        <ContainerDiv>
            <ButtonsContainer>
                <When condition={content.length > 1}>
                    <ScrollButton
                        disableLeft={!isContentLeft}
                        onClick={(e) => {
                            scroll(e, true);
                            onPrevClick();
                        }}
                        direction="left"
                    />
                    <ScrollButton
                        disableRight={disableRightArrow}
                        onClick={(e) => {
                            scroll(e);
                            onNextClick();
                        }}
                        direction="right"
                    />
                </When>
            </ButtonsContainer>
            <MobileButtonContainer height={height}>
                <When condition={content.length > 1}>
                    <ScrollButton
                        disableLeft={!isContentLeft}
                        onClick={(e) => {
                            scroll(e, true);
                            onPrevClick();
                        }}
                        direction="left"
                        style={{
                            boxShadow: 'var(--rwl-shadow-lg)',
                            visibility: !isContentLeft ? 'hidden' : 'visible',
                            backgroundColor: 'var(--rwl-palette-white)',
                        }}
                    />
                    <ScrollButton
                        disableRight={disableRightArrow}
                        onClick={(e) => {
                            scroll(e);
                            onNextClick();
                        }}
                        direction="right"
                        style={{
                            visibility: disableRightArrow
                                ? 'hidden'
                                : 'visible',
                            boxShadow: 'var(--rwl-shadow-lg)',
                            backgroundColor: 'var(--rwl-palette-white)',
                        }}
                    />
                </When>
            </MobileButtonContainer>
            <CarouselContainerDiv
                height={height}
                ref={setContainerRef}
                onScroll={onScroll}
                role="list"
            >
                {content.map((c) => render(c))}
            </CarouselContainerDiv>
        </ContainerDiv>
    );
};

function calculateNumberOfContentInView(
    scrollContainer: HTMLElement | undefined,
    totalContentCount: number
) {
    if (!scrollContainer || totalContentCount < 1) {
        return 0;
    }

    const fullScrollWidth = scrollContainer?.scrollWidth ?? 0;
    const perContentWidth = fullScrollWidth / totalContentCount;
    const viewableWidth = scrollContainer?.clientWidth ?? 0;
    const numberOfContentInView = viewableWidth / perContentWidth;

    return Math.min(totalContentCount, Math.round(numberOfContentInView));
}
